import { useRef, useEffect, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ChatMessage } from '../../services/chatService';
import ChatMessageItem, { DateSeparator, isSameDay } from './ChatMessageItem';
import Spinner from '../ui/Spinner';

// CHG-131 REQ-CP-015: 슬랙형 그룹핑 임계 5분
const GROUPING_INTERVAL_MS = 5 * 60_000;

interface ChatMessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  currentUserId: string | undefined;
  onMentionClick?: (issueKey: string) => void;
  onMentionCardClick?: (issueId: string) => void;
  showNewMsgBtn: boolean;
  onNewMsgBtnClick: () => void;
  /** Ref forwarded for external scroll-to-bottom */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  /** Called when scroll position changes (isAtBottom) */
  onScrollChange?: (isAtBottom: boolean) => void;
  /** Compact mode for side panel */
  compact?: boolean;
  /** Message ID to highlight (search jump) */
  highlightedMessageId?: string | null;
  /** Channel ID for edit/delete */
  channelId?: string;
  /** Thread panel open callback */
  onOpenThread?: (messageId: string) => void;
  /** Current user's role in the channel */
  currentUserRole?: string | null;
  /** Called when a message is pinned/unpinned */
  onPinChange?: () => void;
  /** Channel is archived (read-only) */
  isArchived?: boolean;
  // CHG-123 REQ-V123-021: 메시지 → 작업/할 일 변환 컨텍스트
  channelType?: 'PROJECT' | 'USER' | 'DM';
  contextProjectId?: string;
  contextProjectKey?: string;
}

export default function ChatMessageList({
  messages,
  isLoading,
  loadingMore,
  hasMore,
  onLoadMore,
  currentUserId,
  onMentionClick,
  onMentionCardClick,
  showNewMsgBtn,
  onNewMsgBtnClick,
  scrollContainerRef,
  messagesEndRef,
  onScrollChange,
  compact,
  highlightedMessageId,
  channelId,
  onOpenThread,
  currentUserRole,
  onPinChange,
  isArchived,
  channelType,
  contextProjectId,
  contextProjectKey,
}: ChatMessageListProps) {
  const topSentinelRef = useRef<HTMLDivElement>(null);

  // ─── 무한 스크롤: Intersection Observer ───
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !isLoading) {
          onLoadMore();
        }
      },
      { root: container, threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, isLoading, onLoadMore, scrollContainerRef]);

  // ─── 스크롤 감지 ───
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || !onScrollChange) return;

    const handleScroll = () => {
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
      onScrollChange(isAtBottom);
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef, onScrollChange]);

  // ─── 메시지 그룹핑 ───
  // CHG-131 REQ-CP-015: 같은 사용자라도 그룹 시작 후 5분이 지나면 새 그룹으로 분리.
  // (직전 메시지가 아니라 그룹 첫 메시지 기준 — 30초 간격으로 계속 보내도 5분 후엔 break)
  const shouldShowSender = useMemo(() => {
    const result: boolean[] = [];
    let groupStartMs: number | null = null;
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const prev = i > 0 ? messages[i - 1] : null;
      const msgMs = new Date(msg.createdAt).getTime();
      let show = false;
      if (!prev) {
        show = true;
      } else if (prev.sender.userId !== msg.sender.userId) {
        show = true;
      } else if (!isSameDay(prev.createdAt, msg.createdAt)) {
        show = true;
      } else if (groupStartMs !== null && msgMs - groupStartMs > GROUPING_INTERVAL_MS) {
        show = true;
      }
      if (show) groupStartMs = msgMs;
      result.push(show);
    }
    return result;
  }, [messages]);

  return (
    <>
      <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto min-h-0 py-3 ${compact ? 'px-3' : 'px-5'}`}>
        {/* 무한 스크롤 상단 감지 */}
        <div ref={topSentinelRef} className="h-1" />

        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner className="size-5" />
          </div>
        )}

        {!hasMore && messages.length > 0 && (
          <div className="text-center text-sm text-gray-400 py-2">이전 메시지가 없습니다</div>
        )}

        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <p className="text-sm">아직 메시지가 없습니다.</p>
            <p className="text-sm mt-1">첫 메시지를 보내보세요!</p>
          </div>
        )}

        {messages.map((msg, i) => {
          const isMine = msg.sender.userId === currentUserId;
          const showDate = i === 0 || !isSameDay(messages[i - 1].createdAt, msg.createdAt);
          const showSender = shouldShowSender[i];

          return (
            <div key={msg.messageId} id={`msg-${msg.messageId}`}>
              {showDate && <DateSeparator date={msg.createdAt} />}
              <ChatMessageItem
                message={msg}
                isMine={isMine}
                showSender={showSender}
                onMentionClick={onMentionClick}
                onMentionCardClick={onMentionCardClick}
                compact={compact}
                isHighlighted={msg.messageId === highlightedMessageId}
                channelId={channelId}
                currentUserId={currentUserId}
                onOpenThread={onOpenThread}
                currentUserRole={isArchived ? null : currentUserRole}
                onPinChange={isArchived ? undefined : onPinChange}
                channelType={channelType}
                contextProjectId={contextProjectId}
                contextProjectKey={contextProjectKey}
              />
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* 새 메시지 플로팅 버튼 */}
      {showNewMsgBtn && (
        <div className="relative">
          <button
            onClick={onNewMsgBtnClick}
            className="absolute bottom-2 right-4 bg-blue-500 text-white rounded-full px-4 py-2 shadow-lg text-sm font-medium
              flex items-center gap-1 hover:bg-blue-600 transition-colors z-10"
          >
            새 메시지 <ChevronDown className="size-4" />
          </button>
        </div>
      )}
    </>
  );
}
